import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ERoutes } from '../../../routes';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../store';
import { ECustomerTypeView } from '../../../store/ducks/user/types';
import { Menu } from '@gerdau/hefesto-mais-web/Menu';
import { IMenuOption } from '@gerdau/hefesto-mais-web/Menu/Menu.types';
import { MenuContainer } from './styles';
import { sendTagging } from '../../../store/ducks/tagging/actions';
import { GetFlag } from '../../../utils/featureFlag';
import { useNavigationGuard } from '../../../providers/navigationguard.provider';

interface HeaderTabProps {
  verticalTabs?: boolean;
  selectedTab: ERoutes;
  onSelectTab(selectedTab: ERoutes): void;
}

interface TabsList {
  name: string | JSX.Element;
  path: ERoutes;
  hasUserPermission: boolean;
  customerViews: ECustomerTypeView[];
  itemName: string;
  itemDescription: string;
}

const HeaderTabs: React.FC<HeaderTabProps> = ({ onSelectTab }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const navigationGuard = useNavigationGuard();
  const JOURNEYVIEW_TYPE = 'commerce_magento';

  const [allowedTabs, setAllowedTabs] = useState<IMenuOption[]>([]);
  const user = useSelector((state: ApplicationState) => state.user);
  const customerViewType = useSelector(
    (state: ApplicationState) => state.user.searchCustomerType
  );
  const campanha_loyalty_geral = GetFlag('campanha_loyalty_geral');

  const cartQuantity = useSelector(
    (state: ApplicationState) => state.cartItemsMvp?.quantity || 0
  );

  const shoppingCartItems = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart.items.length
  );

  const isMVPUser =
    user.journeyViews && user.journeyViews.includes(JOURNEYVIEW_TYPE);

  const cartItemsCount = isMVPUser ? cartQuantity : shoppingCartItems;

  const getTabList = (): TabsList[] => {
    return [
      {
        name: 'Comprar',
        path: ERoutes.PURCHASE,
        customerViews: [
          ECustomerTypeView.EMITTER,
          ECustomerTypeView.RECEIVER,
          ECustomerTypeView.FABRICATION_RECEIVER
        ],
        hasUserPermission:
          (user.searchCustomerType === ECustomerTypeView.EMITTER ||
            user.searchCustomerType ===
              ECustomerTypeView.FABRICATION_RECEIVER) &&
          user.userPermissions.hasOrderInputAccess,
        itemName: 'button:comprar',
        itemDescription: 'menu_comprar'
      },
      {
        name: 'Painel de gestão',
        path: ERoutes.DASHBOARD,
        customerViews: [
          ECustomerTypeView.EMITTER,
          ECustomerTypeView.FABRICATION_RECEIVER
        ],
        hasUserPermission:
          user.userPermissions.hasOrderViewAccess ||
          user.userPermissions.hasFinancialAccess,
        itemName: 'button:painel_gestao',
        itemDescription: 'menu_painelGestao'
      },
      {
        name: 'Pedidos',
        path: ERoutes.ORDERS,
        customerViews: [
          ECustomerTypeView.EMITTER,
          ECustomerTypeView.RECEIVER,
          ECustomerTypeView.FABRICATION_RECEIVER
        ],
        hasUserPermission:
          user.userPermissions.hasOrderViewAccess || user.isManager,
        itemName: 'button:pedidos',
        itemDescription: 'menu_pedidos'
      },
      {
        name: 'Finanças',
        path: ERoutes.FINANCIALS,
        customerViews: [
          ECustomerTypeView.EMITTER,
          ECustomerTypeView.RECEIVER,
          ECustomerTypeView.FABRICATION_RECEIVER
        ],
        hasUserPermission:
          user.userPermissions.hasFinancialAccess || user.isManager,
        itemName: 'button:financas',
        itemDescription: 'menu_financas'
      },
      {
        name: 'Maquetes e Projetos',
        path: ERoutes.PROJECT_MOCKUP,
        customerViews: [
          ECustomerTypeView.EMITTER,
          ECustomerTypeView.FABRICATION_RECEIVER
        ],
        hasUserPermission:
          user.userPermissions.hasOrderInputAccess &&
          user.userPermissions.hasFabricationAccess,
        itemName: 'button:maquetes_e_projetos',
        itemDescription: 'menu_maquetesEProjetos'
      },
      {
        name: 'Contratos e obras',
        path: ERoutes.CONTRACTS_AND_CONSTRUCTIONS,
        customerViews: [
          ECustomerTypeView.EMITTER,
          ECustomerTypeView.FABRICATION_RECEIVER
        ],
        hasUserPermission:
          user.userPermissions.hasOrderInputAccess &&
          user.userPermissions.hasFabricationAccess,
        itemName: 'button:contratos_e_obras',
        itemDescription: 'menu_contratosEObras'
      },

      {
        name: 'Buscar documentos',
        path: ERoutes.DOWNLOAD_AREA,
        customerViews: [
          ECustomerTypeView.EMITTER,
          ECustomerTypeView.RECEIVER,
          ECustomerTypeView.FABRICATION_RECEIVER
        ],
        hasUserPermission: true,
        itemName: 'button:buscar_documentos',
        itemDescription: 'menu_buscarDocumentos'
      },

      {
        name: 'Campanha Gerdau',
        path: ERoutes.LOYALTY,
        customerViews: [
          ECustomerTypeView.EMITTER,
          ECustomerTypeView.RECEIVER,
          ECustomerTypeView.FABRICATION_RECEIVER
        ],
        hasUserPermission:
          user.userPermissions.hasLoyaltyAccess && campanha_loyalty_geral,
        itemName: 'button:campanha_gerdau',
        itemDescription: 'menu_campanhaGerdau'
      }
    ];
  };
  const tabsList = getTabList();

  const onTabChange = (path: string) => {
    history.push(path);

    const selectedTab: ERoutes = path as ERoutes;
    onSelectTab(selectedTab);
  };

  useEffect(() => {
    const filteredTabs = tabsList.filter(
      (tab) =>
        tab.hasUserPermission && tab.customerViews.includes(customerViewType)
    );
    const formattedData: IMenuOption[] = filteredTabs.map((tab) => ({
      active: window.location.pathname.includes(tab.path),
      title: tab.name,
      onClick: () => {
        const lastScreenName = window.dataLayer
          .filter((data: { event: string }) => data.event === 'page_view')
          .pop()?.screenName;

        dispatch(
          sendTagging({
            screenName: lastScreenName,
            event: 'click',
            itemName: tab.itemName,
            itemDescription: tab.itemDescription
          })
        );

        navigationGuard.handleCallFunctions({
          action: () => onTabChange(tab.path),
          hasItemOnCart: cartItemsCount > 0
        });
      }
    }));
    setAllowedTabs(formattedData);
  }, [window.location.pathname, campanha_loyalty_geral, cartItemsCount]);

  return (
    <MenuContainer>
      <Menu data-testid="hefesto-menu" menuOptions={allowedTabs} />
    </MenuContainer>
  );
};

export default HeaderTabs;
